import { useContext } from 'react'
import PropTypes from 'prop-types'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject, isValidPrice } from '@domain-group/javascript-utils'

import useAgency, { useAgencyForm } from '@hooks/useAgency'
import usePricingPlans from '@hooks/usePricingPlans'

import ModalSubHeader from '@components/ModalComponents/ModalSubHeader'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

const ManageAgencyPricingPlanModal = (props) => {
  const { callbacks, modalKey, showModal } = props
  const { closeModal, updateAgency } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { agency: initAgency } = modalPayload

  const agencyPayload = useAgency(initAgency)
  const {
    agency,
    agency: {
      title: agencyTitle,
    },
    brand: { title: brandTitle },
    callbacks: {
      updateAgency: updateFn,
    },
    updating,
  } = agencyPayload

  const agencyFormPayload = useAgencyForm(agency, { validateOn: 'change' })

  const {
    entityState,
    entityState: {
      campaign_fee,
      campaign_fee_cap,
      pricing_plan_id,
    },
    errors,
    handlers,
  } = agencyFormPayload

  const { filteredPricingPlans: pricingPlans } = usePricingPlans()

  const updateAgencyPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: defaultRequestOptions.agency,
    toastText: 'Pricing Plan Updated',
  }

  const saveEnabled = isValidPrice(campaign_fee)

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header
        callbacks={callbacks}
        title="Manage"
        titleSecondLine="Pricing Plan"
      />

      <ModalSubHeader
        ownerTitle={agencyTitle}
        title="Managing Pricing Plan for"
        subTitle={brandTitle}
      />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="Pricing Plan">
            <select
              data-value-type="number"
              name="pricing_plan_id"
              value={pricing_plan_id}
              {...handlers}
            >
              <option value="">Please Select...</option>
              {pricingPlans.map(pricingPlan => (
                <option key={pricingPlan.id} value={pricingPlan.id}>
                  {pricingPlan.title}
                </option>
              ))}
            </select>
          </Form.Field>

          <Form.Field
            errorMessage={errors.campaign_fee}
            label="* Campaign Fee"
            marginTop="large"
          >
            <input
              min="0"
              name="campaign_fee"
              type="number"
              value={campaign_fee}
              {...handlers}
            />
          </Form.Field>

          <Form.Field label="Campaign Fee Cap" marginTop="large">
            <input
              min="0"
              name="campaign_fee_cap"
              type="number"
              value={campaign_fee_cap}
              {...handlers}
            />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={updating}
          onClick={() => updateAgency(updateAgencyPayload)}
          size="large"
        >
          {saveEnabled ? 'Save Pricing Plan' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

ManageAgencyPricingPlanModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

ManageAgencyPricingPlanModal.defaultProps = {
  modalKey: 'ManageAgencyPricingPlanModal',
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <ManageAgencyPricingPlanModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
